import React, { useEffect } from "react";
import log from "../../../utils/logger";
import { ClauseHeader } from "../components/clause-header";
import ChatInputText from "@/components/chat-input-text/chat-input-text";
import { TextareaOnChangeData } from "@fluentui/react-components";
import { getDocumentText } from "@/taskpane/lib";
import { draftNewClause } from "@/services/chat-service";
import { useDocumentClassification } from "@/context/document-classification";
import ReplyCarousel from "./reply-carousel/reply-carousel";
import { useNewClause } from "@/context/new-clause";
import { useAbortController } from "@/hooks/use-abort-controller";

export default function NewClause() {
  const { selectedPromptConfig } = useDocumentClassification();
  const { createController, abort, cleanup, getSignal } = useAbortController();
  const {
    query,
    setQuery,
    loadingQuery,
    setLoadingQuery,
    queryReply,
    setQueryReply,
    loadingInsertion,
    setLoadingInsertion,
    madeRequest,
    setMadeRequest,
  } = useNewClause();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abort();
      setLoadingQuery(false);
      setLoadingInsertion(false);
      cleanup();
    };
  }, []);

  const onQueryChange = (_ev: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => {
    setQuery(data.value);
  };

  const onQueryDocument = async () => {
    try {
      setMadeRequest(true);
      setQueryReply("");
      if (!query) return;
      createController();
      setLoadingQuery(true);
      const wholeDocument = await getDocumentText();
      const reply = await draftNewClause({
        promptConfigId: selectedPromptConfig.id,
        clauseDescription: query,
        wholeDocument,
        abortSignal: getSignal(),
      });
      setQueryReply(reply);
    } catch (e) {
      if (e.message === "Request was cancelled") {
        log.debug("Request was cancelled by user", "onQueryDocument", e);
        setQueryReply("");
      } else {
        log.error("New clause error", "onQueryDocument", e);
      }
    } finally {
      setLoadingQuery(false);
      setLoadingInsertion(false);
      cleanup();
    }
  };

  const onStopQuery = () => {
    abort();
    setLoadingQuery(false);
    setLoadingInsertion(false);
    cleanup();
  };

  const loading = loadingQuery || loadingInsertion;
  return (
    <div>
      <ClauseHeader title="New Clause" />
      <ChatInputText
        placeholder="Describe the clause you want to draft"
        query={query}
        onQueryChange={onQueryChange}
        sendBtn={{
          onClick: onQueryDocument,
          disabled: !query || loadingQuery,
        }}
        stopBtn={{
          onClick: onStopQuery,
          disabled: !loading,
        }}
        loading={loading}
      />
      {madeRequest && (
        <ReplyCarousel
          loadingInsertion={loadingInsertion}
          setLoadingInsertion={setLoadingInsertion}
          loading={loadingQuery}
          queryReply={queryReply}
        />
      )}
    </div>
  );
}
